<script>
import Icon from "../Icon.vue";
import {useUserStore} from "../../../stores/user";
import {useI18n} from "vue-i18n";
import {useRoute, useRouter} from "vue-router";
import {useOrganizationStore} from "../../../stores/organizations";

export default {
  name: 'DashboardHeader',
  components: {Icon},

  props: {
    collapsed: {
      type: Boolean,
      default: false
    },
    title: {
      type: String,
      required: true
    }
    ,
    // What to show when there's no org logo (for expanded state)
    // 'name' | 'initials' | 'none' | 'app'
    fallback: {
      type: String,
      default: "name",
      validator: (v) => ["name", "initials", "none", "app"].includes(v),
    },
    // Used only when fallback === 'app'
    appLogo: {
      type: String,
      default: null
    },
    ignoreOrg: {
      type: Boolean,
      default: false
    },
  },

  emits: ['menu-click'],

  setup() {
    // Stores
    const userStore = useUserStore();
    const organizationStore = useOrganizationStore();

    // I18n
    const {t} = useI18n();

    // Router
    const router = useRouter();
    const route = useRoute();

    return {
      t,
      router,
      route,
      userStore,
      organizationStore
    };
  },

  data() {
    return {
      isMenuOpen: false
    };
  },

  computed: {
    organization() {
      return this.userStore.currentOrganization || {};
    },
    organizationName() {
      return (this.organization.name || "").trim();
    },
    organizationInitials() {
      if (!this.organizationName) return "";
      return this.organizationName
        .split(/\s+/)
        .map((w) => w[0])
        .slice(0, 2)
        .join("")
        .toUpperCase();
    },
    // Correct logo source for current state
    logoSrc() {
      if (this.ignoreOrg) return null;

      return this.organization.logo_url || null;
    },
    // Collapsed → always show initials if no logo.
    // Expanded → show initials only if explicitly requested via fallback.
    shouldShowInitials() {
      return (
        !this.logoSrc &&
        !!this.organizationInitials &&
        (this.collapsed || this.fallback === "initials")
      );
    },
    // Expanded only: show org name when no logo and fallback is 'name'
    shouldShowName() {
      return (
        !this.collapsed &&
        !this.logoSrc &&
        this.fallback === "name" &&
        !!this.organizationName
      );
    },
    // Center & remove left padding if expanded and no logo
    logoContainerClass() {
      return {
        collapsed: this.collapsed,
        // expanded: !this.collapsed,
        "justify-center": !this.collapsed && !this.logoSrc,
        "pl-0": !this.collapsed && !this.logoSrc,
        "min-w-[256px]": !this.collapsed
      };
    },
    welcomeMessage() {
      if (this.userStore.isBeingImpersonated) {
        return `Impersonating ${this.userStore.user.full_name}`;
      }
      if (this.userStore?.user?.first_name) {
        return `Welcome, ${this.userStore.user.first_name}!`;
      }
      return "Welcome!";
    }
  },

  mounted() {
    document.addEventListener('keydown', this.handleEscape);
  },

  unmounted() {
    document.removeEventListener('keydown', this.handleEscape);
  },

  methods: {
    toggleMenu() {
      this.isMenuOpen = !this.isMenuOpen;
    },
    closeMenu() {
      this.isMenuOpen = false;
    },
    handleEscape(e) {
      if (e.key === 'Escape' && this.isMenuOpen) {
        this.closeMenu();
      }
    }
  }
};
</script>

<template>
  <header class="header">
    <div class="header-container">
      <div class="flex flex-row gap-2">
        <!-- Logo Section -->
        <div class="logo-container h-14 ">
          <!--             :class="logoContainerClass"-->

          <!-- Logo -->
          <img
            v-if="logoSrc"
            :key="logoSrc"
            :src="logoSrc"
            :alt="`${organizationName || 'Logo'}`"
            class="h-10 md:h-12 w-auto max-w-full object-contain"
            :class="{ 'px-0': collapsed, 'pr-2': !collapsed }"
          >

          <!-- Collapsed → initials if no logo -->
          <div
            v-else-if="shouldShowInitials"
            class="flex items-center justify-center h-10 w-10 rounded-md bg-gray-200 text-gray-700 font-semibold select-none"
            :title="organizationName"
          >
            {{ organizationInitials }}
          </div>

          <!-- Expanded → app fallback -->
          <img
            v-else-if="!collapsed && fallback === 'app' && appLogo"
            :src="appLogo"
            alt="App logo"
            class="h-10 md:h-12 w-auto max-w-full object-contain"
            :class="{ 'px-0': collapsed, 'pr-2': !collapsed }"
          >

          <!-- Expanded → org name -->
          <div
            v-else-if="shouldShowName"
            class="flex items-center h-10 font-semibold text-gray-800 text-left leading-tight break-words pl-3"
            style="max-width: 100%"
            :title="organizationName"
          >
            {{ organizationName }}
          </div>

          <!-- Expanded → initials (explicit) -->
          <div
            v-else-if="!collapsed && fallback === 'initials' && organizationInitials"
            class="flex items-center justify-center h-10 w-10 rounded-md bg-gray-200 text-gray-700 font-semibold select-none"
            :title="organizationName"
          >
            {{ organizationInitials }}
          </div>

          <!-- None -->
          <div
            v-else
            class="h-10 w-10"
          />
        </div>

        <!-- Welcome Message - Desktop: inline, Mobile: centered -->
        <div class="hidden md:flex">
          <div class="title-small mt-2 p-2">
            {{ welcomeMessage }}
          </div>
        </div>

        <!-- Welcome Message - Mobile only -->
        <!--        <div class="md:hidden absolute left-1/2 transform -translate-x-1/2 max-w-[calc(100vw-120px)] px-2">-->
        <div class="md:hidden absolute inset-y-0 right-10 flex items-center">
          <div class="title-medium text-sm truncate text-right">
            {{ welcomeMessage }}
          </div>
        </div>
        <div v-if="userStore.isBeingImpersonated">
          <button
            class="btn btn-warning btn-filled m-3"
            @click="userStore.stopImpersonation()"
          >
            Stop Impersonating
          </button>
        </div>
      </div>
      <!-- Hamburger Menu -->
      <div class="menu-section mr-1 md:mr-8">
        <button
          class="hamburger-btn"
          :class="{ active: isMenuOpen }"
          aria-label="Toggle menu"
          @click="toggleMenu"
        >
          <icon
            :size="'20px'"
            class="md:!w-6 md:!h-6 "
            icon="bars"
          />
        </button>

        <!-- Dropdown Menu -->
        <div
          class="dropdown-menu"
          :class="{ show: isMenuOpen }"
        >
          <div class="nav-menu">
            <slot name="nav-menu-items" />
          </div>
        </div>
      </div>
    </div>
  </header>
</template>

<style lang="scss" scoped>
.header {
  @apply relative bg-white z-[10] shadow-md;
}

.header-container {
  @apply flex justify-between items-center px-2;
  height: 7dvh;
}

/* Logo Section */
.logo-container {
  @apply flex items-center;

  &.collapsed {
    width: 60px;
  }

  &.expanded {
    @apply w-64;
    //@apply pl-8;
  }
}

/* Menu Section */
.menu-section {
  @apply relative;
}

/* Dropdown Menu */
.dropdown-menu {
  @apply absolute top-full right-0 bg-white border border-gray-200 rounded-lg shadow-lg;
  @apply mt-2 transition-all duration-300 ease-in-out overflow-hidden;
  @apply invisible opacity-0;
  @apply min-w-[100px] md:min-w-[150px];
  transform: translateY(-10px);
}

.dropdown-menu.show {
  @apply opacity-100 visible;
  transform: translateY(0);
}

.nav-menu {
  @apply flex flex-col;
}

//Not sure how small we want it to be in mobile view. take out text-xs if its too small
.nav-item, :deep(.nav-item) {
  @apply p-2 md:p-4 text-gray-700 no-underline text-xs md:text-sm transition-colors duration-200 ease-in-out cursor-pointer;

  &:hover {
    @apply bg-gray-50 text-gray-900;
  }

  &:focus {
    @apply outline-2 outline-blue-500 outline-offset-[-2px] bg-gray-50;
  }
}

/* Mobile Overlay */


.menu-overlay.show {
  @apply opacity-100 visible;
}


</style>
